import React from 'react';
import { Html } from '@react-three/drei';
import { useModelStore } from '../../../../store/useModelStore';
import { useUIStore } from '../../../../store/useUIStore';
import { scaleToGrid, getScaleFromRange } from '@/utils/gridScale';

/**
 * BeamIDLabels Component
 * 
 * Displays all beam IDs in the format "B{id}" when the showBeamIds toggle is enabled.
 * Uses the same formatting as NodeIdTag for consistency.
 */
export const BeamIDLabels: React.FC = () => {
  const nodes = useModelStore((state) => state.nodes);
  const beams = useModelStore((state) => state.beams);
  const showBeamIds = useUIStore((state) => state.showBeamIds);
  const scaleRange = useUIStore((state) => state.scaleRange);

  // Get scaling factor for coordinates
  const scaleFactor = getScaleFromRange(scaleRange);

  if (!showBeamIds || !beams || beams.length === 0 || !nodes || nodes.length === 0) {
    return null;
  }

  return (
    <>
      {beams.map((beam) => {
        const startNode = nodes.find((n) => n.id === beam.startNodeId);
        const endNode = nodes.find((n) => n.id === beam.endNodeId);

        if (!startNode || !endNode) {
          return null;
        }

        // Calculate midpoint of the beam with scaling
        const midX = (scaleToGrid(startNode.x, scaleFactor) + scaleToGrid(endNode.x, scaleFactor)) / 2;
        const midY = (scaleToGrid(startNode.y, scaleFactor) + scaleToGrid(endNode.y, scaleFactor)) / 2;
        const midZ = (scaleToGrid(startNode.z, scaleFactor) + scaleToGrid(endNode.z, scaleFactor)) / 2;

        return (
          <Html
            key={`beam-label-${beam.id}`}
            position={[midX, midY, midZ + 0.3]}
            center
            zIndexRange={[0, 10]}
          >
            <div
              style={{
                pointerEvents: 'none',
                userSelect: 'none',
                fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica Neue, Arial',
                fontSize: '16px',
                fontWeight: 600,
                color: '#dc2626',
                textShadow: '0 0 2px white, 0 0 4px white',
                whiteSpace: 'nowrap',
              }}
            >
              B{beam.id}
            </div>
          </Html>
        );
      })}
    </>
  );
};
